import React, {
    forwardRef,
    useImperativeHandle,
    useState,
    useEffect,
} from "react";

interface SendEmailModalProps {
    canSendEmail: boolean;
    loading: boolean;
    onClose: () => void;
    onSendEmail: (data: { name: string; email: string }) => void;
    onSelectAll: () => void;
}

interface SendEmailModalHandle {
    resetForm: () => void;
}

const SendEmailModal = forwardRef<SendEmailModalHandle, SendEmailModalProps>(
    ({ canSendEmail, loading, onClose, onSendEmail, onSelectAll }, ref) => {
        const [name, setName] = useState("");
        const [email, setEmail] = useState("");
        const [currentStep, setCurrentStep] = useState<
            "infoPopup" | "selectingArtwork" | "formModal" | null
        >("infoPopup");
        const [errors, setErrors] = useState<{ name: boolean; email: boolean }>({
            name: false,
            email: false,
        });

        useImperativeHandle(ref, () => ({
            resetForm: () => {
                setName("");
                setEmail("");
                setCurrentStep("selectingArtwork");
                setErrors({ name: false, email: false });
            },
        }));

        const changeStep = (
            step: "infoPopup" | "selectingArtwork" | "formModal" | null
        ) => {
            setCurrentStep(step);
        };

        const focusInput = (field: "name" | "email") => {
            // Handle focus if needed
        };

        const handleClose = () => {
            onClose();
            resetForm();
        };

        const handleSelectAll = () => {
            onSelectAll();
        };

        const handleSendEmail = () => {
            const newErrors = {
                name: name.trim() === "",
                email: !/^\S+@\S+\.\S+$/.test(email),
            };
            setErrors(newErrors);
            if (!newErrors.name && !newErrors.email && !loading) {
                onSendEmail({ name, email });
            }
        };

        const resetForm = () => {
            setName("");
            setEmail("");
            setCurrentStep("selectingArtwork");
            setErrors({ name: false, email: false });
        };

        // Determine if overlay should be visible
        const shouldShowOverlay =
            currentStep === "infoPopup";

        return (
            <>
                {/* Background Overlay */}
                {shouldShowOverlay && (
                    <div className="fixed top-0 left-0 w-full h-screen bg-black bg-opacity-60 z-20"></div>
                )}

                {/* Info Popup */}
                {currentStep === "infoPopup" && (
                    <div className={'top-0 left-0 w-full h-screen px-2 absolute flex items-center justify-center'}>
                        <div
                            className="text-center p-6 border-2 border-gray-300 rounded text-white flex flex-col bg-black bg-opacity-60 backdrop--tinted max-w-lg z-30 absolute">
                            <h2 className="text-xl font-semibold mb-2">
                                BEWAAR JE FAVORIETEN
                            </h2>
                            <p className="mb-2">
                                Selecteer de kunstwerken die je graag zou willen bewaren en
                                verstuur deze naar jouzelf of naar iemand met wie je deze wilt
                                delen.
                            </p>
                            <span className="d-icon d-network-policy text-7xl"></span>
                            <span
                                className="underline cursor-pointer mt-4"
                                onClick={() => changeStep("selectingArtwork")}
                            >
                    Volgende
                  </span>
                        </div>
                    </div>
                )}

                {/* Email Form */}
                {currentStep === "formModal" && (
                    <div className={'top-0 left-0 px-4 md:px-0 w-full h-screen md:h-full absolute flex items-start justify-center z-30 bg-black/60'}>
                    <div
                        className="text-center p-4 md:p-8 border border-white rounded text-white flex flex-col backdrop--tinted max-w-xl mt-10 md:mt-32 items-center relative">
                        <button
                            className="absolute text-center p-2 cursor-pointer right-0 translate-x-1/2 -translate-y-1/2 bg-black top-0 border border-white rounded-full text-white flex flex-col"
                            onClick={handleClose}
                            aria-label="Close"
                        >
                            <span className="d-icon flex d-close"></span>
                        </button>
                        <h2 className="text-xl font-semibold mb-2">
                            VERSTUREN VIA EMAIL
                        </h2>
                        <p className="mb-3">
                            Voer hier de naam en het e-mailadres van de ontvanger in.
                        </p>

                        <div className="w-full">
                            <div className="-mb-px">
                                <input
                                    className={`shadow appearance-none border rounded-t-lg w-full py-2 px-3 text-white bg-black leading-tight focus:outline-none focus:shadow-outline ${
                                        errors.name ? "border-red-500" : ""
                                    }`}
                                    onFocus={() => focusInput("name")}
                                    onBlur={() => {
                                    }}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    type="text"
                                    placeholder="Naam*"
                                />
                            </div>
                            <div className="mb-4">
                                <input
                                    className={`appearance-none border rounded-b-lg w-full py-2 px-3 text-white bg-black mb-3 leading-tight focus:outline-none ${
                                        errors.email ? "border-red-500" : ""
                                    }`}
                                    onFocus={() => focusInput("email")}
                                    onBlur={() => {
                                    }}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    type="email"
                                    placeholder="E-mailadres*"
                                />
                            </div>

                            <div className="mb-4 flex">
                                <div className="flex items-center">
                                    <input
                                        id="terms"
                                        type="checkbox"
                                        className="checkbox mr-2"
                                    />
                                    <label
                                        className="text-left font-medium text-sm"
                                        htmlFor="terms"
                                    >
                                        Hierbij verleen ik toestemming aan Museum Arnhem tot
                                        verwerking van bovenstaande gegevens.
                                    </label>
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            className={`flex justify-center items-center p-2 border-2 rounded-full text-white ${
                                canSendEmail && !loading
                                    ? "hover:border-yellow-400"
                                    : "opacity-50 pointer-events-none"
                            }`}
                            onClick={handleSendEmail}
                        >
                            {loading ? (
                                <span className="d-icon d-refresh animate-spin mr-2"></span>
                            ) : (
                                <span className="d-icon d-send mr-2"></span>
                            )}
                            <span className="flex items-center justify-center expanded-text">
                  Stuur e-mail
                </span>
                        </button>
                    </div>
                    </div>
                )}

                {/* Desktop Selection */}
                {currentStep === "selectingArtwork" && (
                    <div className={'bg-black/60 z-30 bottom-6 left-6 hidden absolute md:flex px-6 py-4 border border-white rounded text-white transition backdrop--tinted'}>
                    <div className="flex flex-col p-3 ">
                        <div className="mb-3 text-white">
                            Selecteer de kunstwerken die je wilt delen
                        </div>

                        <button
                            type="button"
                            className="flex content-center  items-center p-2 mb-4 border border-white rounded-full text-white text-sm hover:bg-opacity-75"
                            onClick={onSelectAll}
                        >
                            <span className="d-icon d-check ml-2 mr-2 flex items-start justify-center"></span>
                            <span className="flex items-center justify-center expanded-text">
                  Alles selecteren
                </span>
                        </button>

                        <button
                            type="button"
                            className={`flex content-center items-center p-2 mb-4 border border-white rounded-full text-white text-sm ${
                                !canSendEmail
                                    ? "pointer-events-none opacity-50"
                                    : "hover:border-yellow-400"
                            }`}
                            onClick={() => changeStep("formModal")}
                        >
                            <span className="d-icon d-send ml-2 mr-2 flex items-start justify-center"></span>
                            <span className="flex items-center justify-center expanded-text">
                  Versturen via e-mail
                </span>
                        </button>

                        <button
                            type="button"
                            className="flex content-center items-center p-2 border border-white rounded-full text-white text-sm hover:bg-opacity-75"
                            onClick={handleClose}
                        >
                            <span className="d-icon d-close ml-2 mr-2 flex items-start justify-center"></span>
                            <span className="flex items-center justify-center expanded-text">
                  Stoppen
                </span>
                        </button>
                    </div>
                    </div>
                )}

                {/* Mobile Selection */}
                {currentStep === "selectingArtwork" && (
                    <div
                        className="fixed bottom-0 left-0 w-full px-2 py-4 border-t border-white rounded-t-lg text-white backdrop--tinted z-30 md:hidden">
                        <div className="flex flex-col p-2 modal-send-email_actions bg-black bg-opacity-80 rounded-t-lg">
                            <div className="mb-2">
                                Selecteer de kunstwerken die je wilt delen
                            </div>

                            <button
                                type="button"
                                className="flex content-center items-center p-2 mb-2 border border-white rounded-full text-white text-sm hover:bg-opacity-75"
                                onClick={onSelectAll}
                            >
                                <span className="d-icon d-check mr-2"></span>
                                <span className="flex items-center justify-center expanded-text">
                  Alles selecteren
                </span>
                            </button>

                            <button
                                type="button"
                                className={`flex content-center items-center p-2 mb-2 border border-white rounded-full text-white text-sm ${
                                    !canSendEmail
                                        ? "pointer-events-none opacity-50"
                                        : "hover:border-yellow-400"
                                }`}
                                onClick={() => changeStep("formModal")}
                            >
                                <span className="d-icon d-send mr-2"></span>
                                <span className="flex items-center justify-center expanded-text">
                  Versturen via e-mail
                </span>
                            </button>

                            <button
                                type="button"
                                className="flex content-center items-center p-2 border border-white rounded-full text-white text-sm hover:bg-opacity-75"
                                onClick={handleClose}
                            >
                                <span className="d-icon d-close mr-2"></span>
                                <span className="flex items-center justify-center expanded-text">
                  Stoppen
                </span>
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    }
);

export default SendEmailModal;
